'use client';

import { FaDiscord, FaSteam, FaArrowRight, FaHeadset } from 'react-icons/fa';
import { IoMapOutline } from 'react-icons/io5';
import { MdVerified } from 'react-icons/md';
import { useEffect, useState, Suspense, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import Image from 'next/image';

interface SteamProfile {
  steamid: string;
  personaname: string;
  profileurl: string;
  avatar: string;
  avatarmedium: string;
  avatarfull: string;
}

interface SteamSessionData {
  steamId: string;
  profile: SteamProfile | null;
  authenticatedAt: string;
}

function LinkAccountsContent() {
  const searchParams = useSearchParams();
  const sectionRef = useRef<HTMLElement>(null);
  const [steamData, setSteamData] = useState<SteamSessionData | null>(null);
  const [authMessage, setAuthMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('reveal-visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = sectionRef.current?.querySelectorAll('.reveal-hidden');
    cards?.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Check for Steam authentication result
    const steamAuth = searchParams.get('steam_auth');
    const steamError = searchParams.get('steam_error');
    const steamDataParam = searchParams.get('steam_data');

    if (steamAuth === 'success' && steamDataParam) {
      try {
        const data = JSON.parse(decodeURIComponent(steamDataParam));
        console.log('Steam authentication successful, storing session:', data);
        setSteamData(data);
        setAuthMessage({ type: 'success', text: 'Successfully linked Steam account!' });
        
        // Store in localStorage for persistence
        localStorage.setItem('steam_session', JSON.stringify(data));
        console.log('Steam session stored in localStorage');
        
        // Dispatch custom event to notify other components (Header)
        const event = new CustomEvent('steam-session-updated', { detail: data });
        window.dispatchEvent(event);
        console.log('steam-session-updated event dispatched');
        
        // Also trigger storage event manually for same-tab updates
        window.dispatchEvent(new Event('storage'));
        
        // Clear URL parameters after a short delay to ensure Header has time to process
        setTimeout(() => {
          window.history.replaceState({}, '', '/home');
        }, 100);
      } catch (error) {
        console.error('Failed to parse Steam data:', error);
        setAuthMessage({ type: 'error', text: 'Failed to process Steam authentication' });
      }
    } else if (steamError) {
      const errorMessages: Record<string, string> = {
        cancelled: 'Steam authentication was cancelled',
        invalid: 'Steam authentication failed - invalid response',
        no_id: 'Steam authentication failed - no ID received',
        invalid_id: 'Steam authentication failed - invalid Steam ID',
        server_error: 'Steam authentication failed - server error',
      };
      setAuthMessage({ 
        type: 'error', 
        text: errorMessages[steamError] || 'Steam authentication failed' 
      });
      
      // Clear URL parameters
      window.history.replaceState({}, '', '/home');
    }

    // Load existing session from localStorage
    const storedSession = localStorage.getItem('steam_session');
    if (storedSession && !steamData) {
      try {
        const parsedSession = JSON.parse(storedSession);
        console.log('Loaded existing Steam session from localStorage:', parsedSession);
        setSteamData(parsedSession);
      } catch (error) {
        console.error('Failed to load Steam session:', error);
        localStorage.removeItem('steam_session');
      }
    }
  }, [searchParams]);

  const handleSteamLogin = async () => {
    try {
      // Direct navigation to Steam auth endpoint
      window.location.href = '/api/auth/steam';
    } catch (error) {
      console.error('Steam login error:', error);
      alert('Failed to initiate Steam login. Please try again.');
    }
  };

  const handleSteamLogout = () => {
    console.log('Unlinking Steam account');
    setSteamData(null);
    localStorage.removeItem('steam_session');
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('steam-session-updated', { detail: null }));
    window.dispatchEvent(new Event('storage'));
    setAuthMessage({ type: 'success', text: 'Steam account unlinked' });
  };

  return (
    <section ref={sectionRef} className="py-20 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Auth Message */}
        {authMessage && (
          <div
            className={`mb-6 p-4 rounded-xl border-2 transition-all duration-300 ${
              authMessage.type === 'success' ?'bg-green-500/10 border-green-500/30 text-green-400' :'bg-red-500/10 border-red-500/30 text-red-400'
            }`}
          >
            {authMessage.text}
          </div>
        )}

        {/* Main Card with Hexagonal Border */}
        <div className="link-accounts-card-hex reveal-hidden">
          {/* Diagonal accent border - top right */}
          <div 
            className="absolute top-0 right-0 w-40 h-40 pointer-events-none z-0"
            style={{
              background: 'linear-gradient(135deg, rgba(106,92,255,0.25) 0%, transparent 70%)',
              clipPath: 'polygon(100% 0, 100% 100%, 0 0)'
            }}
            aria-hidden="true"
          />

          <div className="relative z-10 p-10 lg:p-14">
            {/* Header */}
            <div className="text-center mb-12">
              <h2 className="text-4xl lg:text-5xl font-900 text-white mb-4" style={{ fontWeight: 900 }}>
                Link Your Accounts
              </h2>
              <p className="text-vanta-muted text-lg max-w-2xl mx-auto">
                Connect your Discord and Steam to access exclusive features and enhance your experience.
              </p>
            </div>

            {/* Steam Account Status */}
            {steamData && steamData.profile && (
              <div className="mb-10 p-6 bg-gradient-to-r from-blue-900/20 to-blue-800/10 rounded-2xl border-2 border-blue-700/40 hover:border-blue-600/60 transition-all duration-300 hover:shadow-[0_10px_40px_rgba(59,130,246,0.2)]">
                <div className="flex items-center gap-5">
                  <div className="relative">
                    <div className="absolute inset-0 bg-blue-500/30 blur-xl rounded-full" />
                    <Image
                      src={steamData.profile.avatarmedium}
                      alt={steamData.profile.personaname}
                      width={80}
                      height={80}
                      className="relative w-20 h-20 rounded-full border-3 border-blue-500 shadow-lg"
                      loading="lazy"
                      quality={75}
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-800 text-xl mb-1" style={{ fontWeight: 800 }}>
                      {steamData.profile.personaname}
                    </p>
                    <p className="text-vanta-muted text-sm">Steam ID: {steamData.steamId}</p>
                  </div>
                  <button
                    onClick={handleSteamLogout}
                    className="text-red-400 hover:text-red-300 text-sm font-700 px-5 py-2.5 rounded-lg border-2 border-red-500/30 hover:border-red-500/60 hover:bg-red-500/10 transition-all duration-200"
                    style={{ fontWeight: 700 }}
                  >
                    Unlink
                  </button>
                </div>
              </div>
            )}

            {/* Benefits Grid - 2 Columns */}
            <div className="grid md:grid-cols-2 gap-6 mb-10">
              {/* Faster Support */}
              <div className="flex items-center gap-5 p-6 bg-[#14141a]/60 rounded-xl border-2 border-white/5 hover:border-green-500/30 transition-all duration-300 hover:shadow-[0_10px_30px_rgba(34,197,94,0.15)] hover:-translate-y-1">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-xl flex items-center justify-center border-2 border-green-500/30 shadow-lg">
                  <FaHeadset className="w-8 h-8 text-green-400" />
                </div>
                <div>
                  <h3 className="text-white font-800 text-lg mb-1" style={{ fontWeight: 800 }}>
                    Faster Support
                  </h3>
                  <p className="text-vanta-muted text-sm">
                    Receive quicker assistance with your verified status.
                  </p>
                </div>
              </div>

              {/* Map Voting Access */}
              <div className="flex items-center gap-5 p-6 bg-[#14141a]/60 rounded-xl border-2 border-white/5 hover:border-vanta-accent/30 transition-all duration-300 hover:shadow-[0_10px_30px_rgba(106,92,255,0.15)] hover:-translate-y-1">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-vanta-accent/20 to-purple-600/10 rounded-xl flex items-center justify-center border-2 border-vanta-accent/30 shadow-lg">
                  <IoMapOutline className="w-8 h-8 text-vanta-accent" />
                </div>
                <div>
                  <h3 className="text-white font-800 text-lg mb-1" style={{ fontWeight: 800 }}>
                    Map Voting Access
                  </h3>
                  <p className="text-vanta-muted text-sm">
                    Participate in deciding the next wipe's map.
                  </p>
                </div>
              </div>

              {/* Verified Status */}
              <div className="flex items-center gap-5 p-6 bg-[#14141a]/60 rounded-xl border-2 border-white/5 hover:border-yellow-500/30 transition-all duration-300 hover:shadow-[0_10px_30px_rgba(234,179,8,0.15)] hover:-translate-y-1">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-yellow-500/20 to-yellow-600/10 rounded-xl flex items-center justify-center border-2 border-yellow-500/30 shadow-lg">
                  <MdVerified className="w-8 h-8 text-yellow-400" />
                </div>
                <div>
                  <h3 className="text-white font-800 text-lg mb-1" style={{ fontWeight: 800 }}>
                    Verified Status
                  </h3>
                  <p className="text-vanta-muted text-sm">
                    Display your verified status in-game.
                  </p>
                </div>
              </div>

              {/* Automatic Discord Sync */}
              <div className="flex items-center gap-5 p-6 bg-[#14141a]/60 rounded-xl border-2 border-white/5 hover:border-[#5865F2]/30 transition-all duration-300 hover:shadow-[0_10px_30px_rgba(88,101,242,0.15)] hover:-translate-y-1">
                <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-[#5865F2]/20 to-[#5865F2]/10 rounded-xl flex items-center justify-center border-2 border-[#5865F2]/30 shadow-lg">
                  <FaDiscord className="w-8 h-8 text-[#5865F2]" />
                </div>
                <div>
                  <h3 className="text-white font-800 text-lg mb-1" style={{ fontWeight: 800 }}>
                    Automatic Discord Sync
                  </h3>
                  <p className="text-vanta-muted text-sm">
                    Keep your roles and perks synced automatically.
                  </p>
                </div>
              </div>
            </div>

            {/* Platform Logos */}
            <div className="flex items-center justify-center gap-8 mb-10">
              {/* Discord Logo */}
              <div className="w-24 h-24 bg-gradient-to-br from-purple-900/30 to-purple-800/20 rounded-2xl flex items-center justify-center border-2 border-purple-700/40 shadow-lg hover:scale-110 transition-transform duration-300">
                <FaDiscord className="w-12 h-12 text-[#5865F2]" />
              </div>

              {/* Connection Line */}
              <div className="w-16 h-0.5 bg-gradient-to-r from-purple-700/50 via-vanta-accent/50 to-blue-700/50" />

              {/* Steam Logo */}
              <div className="w-24 h-24 bg-gradient-to-br from-blue-900/30 to-blue-800/20 rounded-2xl flex items-center justify-center border-2 border-blue-700/40 shadow-lg hover:scale-110 transition-transform duration-300">
                <FaSteam className="w-12 h-12 text-[#1b2838]" />
              </div>
            </div>

            {/* CTA Button */}
            {!steamData ? (
              <button
                onClick={handleSteamLogin}
                className="w-full max-w-md mx-auto block bg-gradient-to-r from-vanta-accent to-purple-600 hover:from-vanta-accent/90 hover:to-purple-600/90 text-white font-800 py-5 px-8 rounded-xl flex items-center justify-center gap-4 transition-all duration-300 transform hover:scale-[1.02] hover:shadow-[0_0_40px_rgba(106,92,255,0.6)] border-2 border-vanta-accent/50"
                style={{ fontWeight: 800 }}
              >
                <FaSteam className="w-6 h-6" />
                <span className="text-lg">Login with Steam</span>
                <FaArrowRight className="w-5 h-5" />
              </button>
            ) : (
              <div className="text-center">
                <div className="inline-flex items-center gap-3 bg-gradient-to-r from-green-500/20 to-green-600/10 text-green-400 font-800 py-4 px-8 rounded-xl border-2 border-green-500/40 shadow-lg" style={{ fontWeight: 800 }}>
                  <span className="text-2xl">✓</span>
                  <span className="text-lg">Steam Account Linked</span>
                </div>
              </div>
            )}

            {/* Footer Text */}
            <p className="text-center text-vanta-muted text-sm mt-8">
              One-click setup. Instant benefits. Secure authentication.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function LinkAccounts() {
  return (
    <Suspense fallback={
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="animate-pulse">
            <div className="h-12 bg-gray-800 rounded w-64 mx-auto mb-4"></div>
            <div className="h-6 bg-gray-800 rounded w-96 mx-auto"></div>
          </div>
        </div>
      </section>
    }>
      <LinkAccountsContent />
    </Suspense>
  );
}